/*
 * Picks the place and moment a round is set at, and precomputes everything the
 * sky viewer needs to draw it.
 *
 * Every round is set where the Sun is below astronomical twilight, so the full
 * catalog is visible.
 *
 * Position is uniform over the whole globe, oceans included. Celestial
 * navigation is a seafaring skill before it is anything else, and excluding
 * water would throw away 71% of the planet and most of the reason the technique
 * exists.
 *
 * The date is the one the player is playing on and the hour is drawn from that
 * night, both disclosed. Picking the place before the hour is what keeps the map
 * uniform, and what stops a player in the Americas at midday from being barred
 * from ever landing near home.
 *
 * The Moon is drawn whenever it happens to be up, but nothing requires it. An
 * earlier Historic tier asked players to date the sky, which the stars alone
 * cannot support — a guess one day late and about one degree too far west
 * reproduces the same star field — so it leaned on the Moon and demanded one be
 * above the horizon. With that tier gone the constraint went with it.
 */

import {
  julianDate,
  planetPositions,
  lstDegrees,
  equatorialToHorizontal,
  moonPosition,
  moonPhase,
  sunAltitude,
  sunHorizontal,
  ASTRONOMICAL_TWILIGHT_DEG
} from './astronomy.mjs';
import { dayStart } from './daily.mjs';
import { haversineKm } from './scoring.mjs';

/*
 * How far apart two rounds of one game have to be.
 *
 * Uniform points on a sphere land close together more often than they feel like
 * they should: two are within 2,000 km of each other about one time in forty, so
 * across the three pairs in a game it happens roughly one game in fourteen. That
 * reads as a bug rather than as chance, and it wastes a round — the second one
 * asks the question the first already asked.
 */
export const MIN_SEPARATION_KM = 2000;
const MAX_ATTEMPTS = 4000;

/*
 * How many hours to try at one place before giving up on it.
 *
 * Night is roughly a third of the day at most latitudes, so a handful of draws
 * would nearly always do. This is generous because the cost of giving up is a
 * silently skewed map: anywhere with a short night would be quietly rejected
 * more often than anywhere with a long one.
 */
const HOUR_ATTEMPTS = 60;


// Uniform over the sphere: latitude must come through asin, or the poles get far
// more than their share of the rounds.
function randomPoint(rng) {
  return {
    lat: (Math.asin(2 * rng() - 1) * 180) / Math.PI,
    lon: rng() * 360 - 180
  };
}

function moonAt(date, lat, lon) {
  const jd = julianDate(date);
  const moon = moonPosition(jd);
  const { alt, az } = equatorialToHorizontal(moon.ra, moon.dec, lat, lstDegrees(jd, lon));
  const phase = moonPhase(jd);
  return { alt, az, illuminated: phase.illuminated, waxing: phase.waxing };
}

export function visibleStars(catalog, date, lat, lon) {
  const lst = lstDegrees(julianDate(date), lon);
  const stars = [];

  for (const [ra, dec, mag, spectralClass, name, bayer, constellation, distance] of catalog) {
    const { alt, az } = equatorialToHorizontal(ra, dec, lat, lst);
    if (alt > 0) stars.push({ alt, az, dec, mag, spectralClass, name, bayer, constellation, distance });
  }

  // Faintest first, so the brightest land on top where they overlap.
  stars.sort((a, b) => b.mag - a.mag);
  return stars;
}

export function visiblePlanets(date, lat, lon) {
  const jd = julianDate(date);
  const lst = lstDegrees(jd, lon);

  return planetPositions(jd)
    .map((planet) => ({
      ...planet,
      ...equatorialToHorizontal(planet.ra, planet.dec, lat, lst)
    }))
    // Uranus sits right at the naked-eye limit and is only ever a faint dot, but
    // it belongs in a sky drawn to magnitude 6.
    .filter((planet) => planet.alt > 0 && planet.mag < 6);
}

/*
 * Constellation figures in the observer's sky.
 *
 * A figure is kept whole if any part of it is above the horizon, vertices below
 * included, so a shape half-set still draws the half that is up rather than
 * snapping off at the horizon line. The label sits at the mean of the vertices
 * that are actually visible.
 */
export function visibleConstellations(figures, date, lat, lon) {
  const lst = lstDegrees(julianDate(date), lon);
  const visible = [];

  for (const figure of figures) {
    const lines = figure.lines.map((line) =>
      line.map(([ra, dec]) => equatorialToHorizontal(ra, dec, lat, lst))
    );

    const above = lines.flat().filter((point) => point.alt > 5);
    if (above.length === 0) continue;

    // Azimuth is circular, so the mean has to go through unit vectors or a
    // figure straddling due north labels itself due south.
    let x = 0;
    let y = 0;
    for (const point of above) {
      x += Math.cos((point.az * Math.PI) / 180);
      y += Math.sin((point.az * Math.PI) / 180);
    }

    visible.push({
      id: figure.id,
      name: figure.name,
      lines,
      label: {
        az: (Math.atan2(y, x) * 180) / Math.PI,
        alt: above.reduce((sum, point) => sum + point.alt, 0) / above.length
      }
    });
  }

  return visible;
}

/*
 * Everything a round needs, once the place and the moment are settled. Shared so
 * an ordinary round and an On This Night round cannot drift apart in what they
 * hand the viewer.
 */
function buildRound({ tier, lat, lon, date, rng, catalog, figures, event = null }) {
  return {
    tier,
    lat,
    lon,
    date,
    event,
    moon: moonAt(date, lat, lon),
    // Below the horizon by construction, but its direction still sets which way
    // the Moon's bright limb points.
    sun: sunHorizontal(lat, lon, date),
    // Randomised so the opening view never doubles as a compass. Facing a fixed
    // direction every round would hand over the meridian, and with it the whole
    // orientation half of the puzzle.
    initialAz: rng() * 360,
    stars: visibleStars(catalog, date, lat, lon),
    planets: visiblePlanets(date, lat, lon),
    constellations: visibleConstellations(figures, date, lat, lon)
  };
}

/*
 * A random hour of the day the game belongs to.
 *
 * That day is the player's *local* calendar date, laid out as a UTC day. Taking
 * it from `now`'s UTC date instead used to hand anyone playing late in the
 * Americas a historical round from their own date and ordinary rounds from the
 * next one, since `eventForDate` has always keyed off the local date.
 */
function randomHourOfDay(rng, now) {
  return new Date(dayStart(now) + rng() * 86400000);
}

/*
 * Pick the place first, then look for an hour of tonight that is dark there.
 *
 * Doing it the other way round — fixing the instant at the real current time and
 * re-rolling the place until one of them happens to be in darkness — restricts
 * every round to the half of the world that is presently in night. For a player
 * in the Americas at midday that is a hard guarantee of never being dropped
 * anywhere near home, which is a strange thing for a game about finding out
 * where you are.
 *
 * Holding the place and searching the clock also makes the map uniform, because
 * every point on the globe gets the same number of chances rather than being
 * weighted by whether it happens to be dark right now. What survives is a real
 * constraint rather than an artefact: somewhere in polar summer has no
 * astronomical night on today's date at any hour, and is genuinely unplayable.
 */
export function generateRound({
  tier,
  catalog,
  figures = [],
  rng = Math.random,
  now = new Date(),
  avoid = []
}) {
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
    const place = randomPoint(rng);

    // Clear of everywhere else this game will visit, including the fixed
    // historical round, whose place is known before any of them are dealt.
    const crowded = avoid.some(
      (other) => haversineKm(place.lat, place.lon, other.lat, other.lon) < MIN_SEPARATION_KM
    );
    if (crowded) continue;

    for (let hour = 0; hour < HOUR_ATTEMPTS; hour += 1) {
      const date = randomHourOfDay(rng, now);
      if (sunAltitude(place.lat, place.lon, date) >= ASTRONOMICAL_TWILIGHT_DEG) continue;

      return buildRound({ tier, lat: place.lat, lon: place.lon, date, rng, catalog, figures });
    }
  }

  return null;
}

/*
 * A round set at a real moment in history rather than at a random point now.
 *
 * The place and the instant both come from the corpus, so nothing is sampled and
 * nothing can be rejected: the entry has already been checked to fall in
 * astronomical night, by the same test the generator applies. Precession is not
 * modelled, so the star field is the J2000 one — about 1.4 degrees per century
 * away from the true sky of that year, which is a few minutes of rotation and
 * costs a careful player a little longitude on the oldest entries.
 */
export function generateEventRound({ tier, event, catalog, figures = [], rng = Math.random }) {
  if (!event) return null;
  const date = new Date(event.at);
  if (Number.isNaN(date.getTime())) return null;

  return buildRound({
    tier,
    lat: event.lat,
    lon: event.lon,
    date,
    rng,
    catalog,
    figures,
    event
  });
}
